import { Link } from "react-router-dom";
import { NeutralMonogram, typeLabel } from "@/components/explore/exploreShared";
import { entryLocation, organizationProfilePath, type DirectoryEntry } from "@/lib/brokerageDirectory";
import { useDirectoryAccountPrompt, type DirectoryAction } from "./DirectoryAccountPrompt";

const ACTION_LABELS: Record<DirectoryAction, string> = {
  compare: "Compare",
  save: "Save",
  message: "Message",
};

/**
 * One directory card: logo or monogram, name, type and city, the cloud badge
 * and a "Basic listing" tag for a staff-set listing (#1039). The name links to
 * the organization's public page.
 *
 * Signed out, Compare / Save / Message open the account prompt. A basic
 * listing has nobody behind it to answer, so it offers no Message. Signed in,
 * the card offers the full profile instead, where those actions live.
 */
export default function BrokerageDirectoryCard({
  entry,
  signedIn,
}: {
  entry: DirectoryEntry;
  signedIn: boolean;
}) {
  const openPrompt = useDirectoryAccountPrompt();
  const href = organizationProfilePath(entry.slug);
  const listed = entry.listing_kind === "listed";
  const actions: DirectoryAction[] = listed ? ["compare", "save"] : ["compare", "save", "message"];
  const meta = [entry.type ? typeLabel(entry.type) : null, entryLocation(entry)].filter(Boolean).join(" · ");

  return (
    <li className="flex flex-col gap-4 rounded-lg border border-border bg-card p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <Link
          to={href}
          tabIndex={-1}
          aria-hidden="true"
          className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border bg-white"
        >
          {entry.logo_url ? (
            <img src={entry.logo_url} alt="" className="h-full w-full object-contain p-1" loading="lazy" />
          ) : (
            <NeutralMonogram name={entry.name} size="sm" className="text-sm" />
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold leading-snug text-ink-900">
            <Link to={href} className="break-words hover:text-primary hover:underline">
              {entry.name}
            </Link>
          </h3>
          {meta && <p className="mt-0.5 text-[13px] text-ink-500">{meta}</p>}
          {(listed || entry.cloud_badge) && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {entry.cloud_badge && (
                <span className="rounded-pill bg-primary-tint px-2 py-0.5 text-[12px] font-medium text-primary">
                  {entry.cloud_badge}
                </span>
              )}
              {listed && (
                <span className="rounded-pill border border-border px-2 py-0.5 text-[12px] font-medium text-ink-700">
                  Basic listing
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-auto flex flex-wrap gap-2">
        {signedIn || !openPrompt ? (
          <Link
            to={href}
            className="inline-flex h-9 items-center rounded-md border border-border px-3 text-[13px] font-medium text-ink-900 hover:bg-muted"
          >
            View profile
          </Link>
        ) : (
          actions.map((action) => (
            <button
              key={action}
              type="button"
              onClick={() => openPrompt(action, entry.name)}
              aria-label={`${ACTION_LABELS[action]} ${entry.name}`}
              className="inline-flex h-9 items-center rounded-md border border-border px-3 text-[13px] font-medium text-ink-900 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {ACTION_LABELS[action]}
            </button>
          ))
        )}
      </div>
    </li>
  );
}
